import { TeamGroupDto } from '@numeo/types-main-web-app'
import { UserRole } from '@numeo/types-core'
import { AnalyticsEvent, EventCategory, trackUnifiedEvent } from 'services/analytics'

/**
 * The role picker's value for "Dispatch Manager". A SENTINEL, never persisted.
 *
 * Dispatch Manager is not a `UserRole`. It is a DISPATCHER who runs at least one group, and the difference
 * matters beyond storage: `UserRole` is read by RBAC defaults, by the extension, by the mobile app and by
 * every seeded permission set, so a fifth member would have to be taught to all of them before a single
 * manager could exist. Deriving it from group ownership instead keeps the enum — and everything that
 * switches on it — exactly as it is, and makes "runs a desk" and "is a Dispatch Manager" the same fact
 * rather than two that can disagree.
 *
 * Chosen to collide with neither a numeric system role (`String(UserRole)`) nor a custom role's ObjectId, so
 * the picker's existing two-branch change handler can tell all three apart.
 */
export const DISPATCH_MANAGER_ROLE_VALUE = 'dispatch-manager'

/** The desks `userId` runs. Empty for everyone else, which is also how a manager is demoted. */
export const managedGroupIdsFor = (groups: TeamGroupDto[], userId: string | undefined): string[] =>
    userId ? groups.filter((group) => group.managers.includes(userId)).map((group) => group.id) : []

/**
 * Is this person a Dispatch Manager?
 *
 * A custom role wins: someone on a custom role is shown as that role, because the picker can only display
 * one value and the custom role is the one an admin deliberately built. They keep their managed desks —
 * the scope is real either way — but the label belongs to the role they were given.
 */
export const isDispatchManager = (
    user: { role?: UserRole | number; rbacRoleId?: string | null } | undefined,
    groups: TeamGroupDto[],
    userId: string | undefined
): boolean => isDispatchManagerRunning(user, managedGroupIdsFor(groups, userId).length > 0)

/**
 * The same question, for a caller that already knows whether this person runs a desk.
 *
 * The sidebar learns it from `useManagedTeamGroups`, which returns only the desks the user MANAGES — so it
 * has the answer without the `managers[]` arrays this file's other entry point needs to derive it. Two ways
 * to supply that one fact, ONE definition of what a Dispatch Manager is: a hand-copied second copy would
 * let the sidebar's menu selection and the role picker's label disagree about the same person.
 */
export const isDispatchManagerRunning = (user: { role?: UserRole | number; rbacRoleId?: string | null } | undefined, runsADesk: boolean): boolean =>
    Boolean(user) && !user?.rbacRoleId && Number(user?.role) === UserRole.DISPATCHER && runsADesk

/**
 * A promotion or demotion made from the ROLE PICKER, on the same event the group editor already emits.
 *
 * The same grant reaches Amplitude from two screens — Settings → Groups (`trackGroupMembersSaved`) and the
 * user modal — and an event that only fires from one of them undercounts exactly the entry point the product
 * expects admins to use. Same event name, same cardinality discipline: counts and a source, never member ids
 * or group names.
 *
 * Swallows its own errors — analytics must never break a user save.
 */
export const trackDispatchManagerRoleSaved = (change: { projectName: string; before: string[]; after: string[]; isNewUser: boolean }): void => {
    try {
        const { projectName, before, after, isNewUser } = change
        const kept = after.filter((id) => before.includes(id)).length
        const promoted = after.length - kept
        const demoted = before.length - kept
        if (promoted === 0 && demoted === 0) return
        trackUnifiedEvent({
            category: EventCategory.FEATURE_USE,
            action: AnalyticsEvent.TEAM_GROUP_MANAGERS_CHANGED,
            project_name: projectName,
            promoted,
            demoted,
            managers: after.length,
            // Which screen the grant came from, so the two sources can be compared rather than summed blind.
            source: 'role_picker',
            // Promoted as part of creating the account, rather than by editing an existing one.
            on_create: isNewUser,
        })
    } catch {
        /* analytics must never break the user modal */
    }
}
